import type { JSX } from 'react';
import { Col, FormGroup, FormSection, Radio, Row, Text } from '@ksyos/design-system';
import type { BariPostEnrollmentData } from '@common/validation/customControls/bariPost/bariPostEnrollment/types';
import { ComplicatieType, Ingreep } from '@common/customControls/bariatrics';
import {
    BooleanRadioGroupFormGroup,
    NumberInputContext,
    RadioGroupFormGroup,
    SelectFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { useProcessActivityEditContext } from '@frontend/utils';

const complicatieTypes: ComplicatieType[] = [
    ComplicatieType.naadlekkage,
    ComplicatieType.bloeding,
    ComplicatieType.overig,
];

const ingrepen: Ingreep[] = [
    Ingreep.gastric_bypass,
    Ingreep.gastric_sleeve,
    Ingreep.gastric_bypass_met_minimizer_ring,
    Ingreep.gastric_sleeve_met_minimizer_ring,
    Ingreep.omegaloop_bypass,
    Ingreep.conversie_sleeve_naar_bypass,
    Ingreep.conversie_maagband_naar_bypass,
];

export function BariPostEnrollmentOperatie(): JSX.Element {
    const { data, errors, onChange } = useProcessActivityEditContext<BariPostEnrollmentData>();

    return (
        <FormSection title="Operatie">
            <TextInputFormGroup
                fieldKey="naam_chirurg"
                isRequired={true}
                label="Naam chirurg"
            />
            <FormGroup
                hasErrors={errors.has('gewicht_voor_operatie')}
                isRequired={true}
                label="Gewicht voor operatie"
            >
                <Row columnGap={3}>
                    <Col style={{ width: '80px' }}>
                        <NumberInputContext
                            fieldKey="gewicht_voor_operatie"
                            maximumFractionDigits={2}
                        />
                    </Col>
                    <Col>
                        <Text
                            fontWeight="medium"
                            mt={2}
                            size="sm"
                        >
                            kg
                        </Text>
                    </Col>
                </Row>
            </FormGroup>
            <SelectFormGroup
                fieldKey="ingreep"
                isRequired={true}
                label="Ingreep"
                onChange={(value) =>
                    onChange(
                        {
                            ingreep: value as Ingreep | null,
                        },
                        'ingreep'
                    )
                }
                options={ingrepen.map((value) => ({
                    label: value,
                    value,
                }))}
                placeholder="Selecteer ingreep"
            />
            <BooleanRadioGroupFormGroup
                fieldKey="complicaties"
                isRequired={true}
                label="Complicaties"
                onChange={async (checked) =>
                    onChange(
                        {
                            complicatie_type: null,
                            complicaties: checked,
                            complicaties_omschrijving: null,
                        },
                        'complicaties'
                    )
                }
                type="radio"
            />
            {data.complicaties && (
                <>
                    <RadioGroupFormGroup
                        fieldKey="complicatie_type"
                        isRequired={true}
                        label="Type complicatie"
                        onChange={async (value) =>
                            onChange(
                                {
                                    complicatie_type: value as ComplicatieType,
                                },
                                !data.complicaties_omschrijving && value === ComplicatieType.overig
                                    ? 'complicatie_type'
                                    : ['complicatie_type', 'complicaties_omschrijving']
                            )
                        }
                    >
                        {complicatieTypes.map((value) => (
                            <Radio
                                key={value}
                                label={value}
                                value={value}
                            />
                        ))}
                    </RadioGroupFormGroup>
                    <TextAreaFormGroup
                        fieldKey="complicaties_omschrijving"
                        isRequired={data.complicatie_type === ComplicatieType.overig}
                        label="Omschrijving complicaties"
                        placeholder="De patient had last van..."
                    />
                </>
            )}
            <BooleanRadioGroupFormGroup
                fieldKey="bijzonderheden"
                isRequired={true}
                label="Bijzonderheden"
                onChange={async (checked) =>
                    onChange(
                        {
                            bijzonderheden: checked,
                            bijzonderheden_omschrijving: null,
                        },
                        'bijzonderheden'
                    )
                }
                type="radio"
            />
            {data.bijzonderheden && (
                <TextAreaFormGroup
                    fieldKey="bijzonderheden_omschrijving"
                    isRequired={true}
                    label="Omschrijving bijzonderheden"
                    placeholder="Het is opvallend dat..."
                />
            )}
        </FormSection>
    );
}
